import { inject, Injectable, Optional, SkipSelf } from '@angular/core';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { EventTargetInterruptSource, Idle, IdleExpiry } from '@ng-idle/core';
import { Keepalive } from '@ng-idle/keepalive';
import { Subscription } from 'rxjs';
import { AppFacadeService } from '../../+app_state';
import { AppSessionDialogComponent } from '../components/app-session-dialog/app-session-dialog.component';

@Injectable({
  providedIn: 'root'
})
export class AppSessionService {
  idleState: string = 'Not started';
  lastPing?: Date = null;
  private subscriptions = new Subscription();

  IDLE_TIMER = 60 * 30; // 30 min
  IDLE_TIMEOUT = 60; //60 seconds
  IDLE_CHECK = 30; //interval - checks user active or not

  modalService = inject(NgbModal);
  appFacade = inject(AppFacadeService);
  private modalRef: NgbModalRef | null = null;

  constructor(
    private idle: Idle,
    private keepalive: Keepalive,
    private expiry: IdleExpiry,
    @Optional() @SkipSelf() parentService: AppSessionService
  ) {
    if (parentService) {
      throw new Error('AppSessionService is already loaded.');
    }
  }

  destroy() {
    this.closeModal();
    this.subscriptions.unsubscribe();
    this.idle.stop();
  }

  initIdle() {
    // this.subscriptions.add(
    //   this.idle.onIdleStart.subscribe(() => {
    //     console.log('User is idle');
    //   })
    // );

    // this.subscriptions.add(
    //   this.idle.onIdleEnd.subscribe(() => {
    //     console.log('User became active again');
    //   })
    // );

    this.subscriptions.add(
      this.idle.onTimeoutWarning.subscribe((countdown: number) => {
        this.idle.clearInterrupts();
        this.updateOrShowModal(countdown);
      })
    );

    this.subscriptions.add(
      this.idle.onTimeout.subscribe(() => {
        // console.log('onTimeout: User has been logged out due to inactivity.');
        this.modalRef.componentInstance.title = 'Session Expired';
        this.modalRef.componentInstance.baseMessage =
          'This session is expired due to inactivity';
        this.modalRef.componentInstance.cancelButtonLabel = 'Sign In';
        this.modalRef.componentInstance.sessionExpired = true;
        this.triggerLogout(false);
      })
    );

    this.idle.setIdle(this.IDLE_TIMER); // time inactivity = idle
    this.idle.setTimeout(this.IDLE_TIMEOUT); // time after idle = logout
    // this.idle.setInterrupts(createDefaultInterruptSources());
    this.setInterrupts();

    this.keepalive.interval(this.IDLE_CHECK);
    this.subscriptions.add(
      this.keepalive.onPing.subscribe(() => {
        this.lastPing = new Date();
      })
    );

    this.idle.watch();
  }

  reset(): void {
    this.idle.stop();
    this.closeModal();
    this.setInterrupts();
    this.idle.watch();
  }

  private updateOrShowModal(countdown: number) {
    if (!this.modalRef) {
      this.modalRef = this.modalService.open(AppSessionDialogComponent, {
        centered: true,
        backdrop: 'static',
        keyboard: false,
        backdropClass: 'blur-backdrop'
      });

      this.modalRef.componentInstance.title = 'Session Expiring';
      this.modalRef.componentInstance.baseMessage =
        'This session is about to expire due to inactivity.\n\nAre you still there?';
      this.modalRef.componentInstance.cancelButtonLabel =
        'Yes, extend my session';

      this.modalRef.result
        .then((confirmed) => {
          if (confirmed) {
            this.reset(); // Extend session only if user confirms
            this.closeModal();
          } else {
            this.triggerLogout(true);
          }
        })
        .catch(() => this.triggerLogout(true)); // Logout if modal is dismissed without action
    }
    this.modalRef.componentInstance.countdown = countdown;
  }

  private closeModal() {
    if (this.modalRef) {
      this.modalRef.close(false);
      this.modalRef = null;
    }
  }

  private triggerLogout(redirectAfterSessionEnd: boolean) {
    if (this.subscriptions) {
      this.idle.stop();
      this.subscriptions.unsubscribe();
    }
    if (redirectAfterSessionEnd) this.closeModal();
    this.appFacade.logout(redirectAfterSessionEnd);
  }

  private setInterrupts() {
    this.idle.setInterrupts([
      new EventTargetInterruptSource(
        document,
        'mousemove mousedown keydown scroll touchstart wheel'
      )
    ]);
  }

  // Validates if the last activity time has expired
  checkLastActive() {
    const expiry = this.expiry?.last();

    // Validate expiry
    if (!(expiry instanceof Date)) {
      this.triggerLogout(true);
      return;
    }

    const expiryTime = expiry.getTime();
    const now = Date.now();
    // If expiry time is in the past -> logout
    if (expiryTime < now) this.triggerLogout(true);
  }
}
